import lodash from 'lodash'
import * as R from 'ramda'
import { useMemo } from 'react'
import { useContextSelector } from 'use-context-selector'

import { FormContext } from './Form'

const mergeCustomize = <T>(objValue: T, srcValue: T) => {
  if (srcValue instanceof File) {
    return srcValue
  }

  if (Array.isArray(objValue) || Array.isArray(srcValue)) {
    return srcValue
  }

  return
}

const resolvePathValue = <D>(data: D, name?: string, depth = 0) => {
  const pathParts = name?.split('.') || []
  const finalName = depth ? pathParts.slice(0, pathParts.length - depth) : pathParts
  return name ? R.path<D>(finalName, data) : data
}

export const useFormDefaultValues = <D>(name?: string, depth = 0) =>
  useContextSelector(FormContext, (context) =>
    resolvePathValue<D>(context?.defaultValues as D, name, depth),
  )

export const useFormInheritValues = <D>(name?: string, depth = 0) =>
  useContextSelector(FormContext, (context) =>
    resolvePathValue<D>(context?.inheritValues as D, name, depth),
  )

export const useFormValues = <D>(name?: string, depth = 0) =>
  useContextSelector(FormContext, (context) =>
    resolvePathValue<D>(context?.values as D, name, depth),
  )

export const useFormData = <D>(name?: string, depth = 0) => {
  const defaultValues = useFormDefaultValues(name, depth)
  const inheritValues = useFormInheritValues(name, depth)
  const values = useFormValues(name, depth)

  return useMemo(() => {
    return lodash.mergeWith({}, defaultValues, inheritValues, values, mergeCustomize)
  }, [defaultValues, inheritValues, values]) as D
}

export const useFormOnChangeValue = () =>
  useContextSelector(FormContext, (context) => context?.onChangeValue)
